import walmartLogo from "assets/walmartLogo.svg";
import amazonLogo from "assets/amazonLogo.svg";
import googleLogo from "assets/googleLogo.svg";
import ebayLogo from "assets/ebayLogo.svg";
import moment from "moment";
import { Tooltip, Link, IconButton } from "@mui/material";
import Launch from "@mui/icons-material/Launch";
import { comparatorAsc } from "utility/helper";

/**
 *
 * @param {number} value The number to display as a currency.
 * @returns A string format with current sign.
 */
const displayCurrency = (params) => {
  return `$${params.value?.toFixed(2)}`;
};

const displayDateFromDateTime = (datetime) => {
  return displayDate({ value: datetime });
};

/**
 *
 * @param {DateValue} param0 The date to display.
 * @returns A string format of human readable date.
 */
const displayDate = ({ value }) => {
  if (!value) {
    return "";
  }

  return moment(value).local().format("LL");
};

/**
 * Display a link in the grid.
 */
const displayLink = (text, url) => {
  return (
    <Tooltip title="View listing">
      <Link
        target="_blank"
        rel="noreferrer"
        underline="hover"
        href={url}
        display="block"
        textOverflow={"ellipsis"}
        overflow={"hidden"}
      >
        {text}
      </Link>
    </Tooltip>
  );
};

/**
 * Display a link in the drawer.
 */
const displayDrawerLink = (text, url) => {
  return (
    <Tooltip title="View listing">
      <Link
        target="_blank"
        rel="noreferrer"
        underline="always"
        href={url}
        display="block"
        textOverflow={"ellipsis"}
        overflow={"hidden"}
      >
        {text}
        <IconButton color="primary" onClick={() => window.open(url, "_blank")}>
          <Launch />
        </IconButton>
      </Link>
    </Tooltip>
  );
};

/**
 * Determine if column contains search term.
 */
const columnContainsSearchTerm = (columnValue, searchTerm) => {
  return columnValue
    ?.toString()
    .toUpperCase()
    .includes(searchTerm?.toUpperCase());
};

/**
 * Quick search apply function
 */
const applyQuickSearchFn = (searchTerm, ...stuff) => {
  let result = false;
  for (const value of stuff) {
    // if any of the values match the search term, return true
    result = result | columnContainsSearchTerm(value, searchTerm);
  }

  return result;
};

/**
 * function to handle sorting when parent rows are present.
 * @param {string} field When a parent row is provided it may represent multiple fields. This is the field to sort by.
 * @param {value} a
 * @param {value} b
 * @param {object} aDetail
 * @param {object} bDetail
 * @returns Result of comparitor, 1, 0, or -1.
 */
const sortWithGroupedRows = (field, a, b, aDetail, bDetail) => {
  if (aDetail?.rowNode?.isAutoGenerated) {
    const { row: aRow } = aDetail.api.getRowParams(aDetail.rowNode.children[0]);
    const { row: bRow } = bDetail.api.getRowParams(bDetail.rowNode.children[0]);
    return comparatorAsc(aRow, bRow, field);
  } else {
    return comparatorAsc(a, b);
  }
};

/**
 *
 * @param {number} number The number to convert to number with ordinal
 * @returns Returns number with ordinal. Ex: 1 => 1st.
 */
const getNumberWithOrdinal = (number) => {
  const s = ["th", "st", "nd", "rd"],
    v = number % 100;
  return number + (s[(v - 20) % 10] || s[v] || s[0]);
};

/**
 *
 * @param {Object} params The params passed to the cell renderer.
 * @returns A logo for the marketplace.
 */
const displayMarketplaceLogo = (market, url) => {
  let image = null;

  switch (market) {
    case "amazon":
      image = amazonLogo;
      break;
    case "walmart":
      image = walmartLogo;
      break;
    case "google":
      image = googleLogo;
      break;
    case "ebay":
      image = ebayLogo;
      break;
    default:
      image = null;
  }

  if (image) {
    return (
      <Tooltip title="View listings">
        <Link target="_blank" rel="noreferrer" href={url} sx={{ pt: 1 }}>
          <img
            src={image}
            alt="Marketplace Logo"
            style={{
              height: "19px",
              objectFit: "contain",
            }}
          />
        </Link>
      </Tooltip>
    );
  } else {
    return market;
  }
};

export {
  displayCurrency,
  displayDate,
  displayLink,
  displayDrawerLink,
  getNumberWithOrdinal,
  displayMarketplaceLogo,
  applyQuickSearchFn,
  sortWithGroupedRows,
  displayDateFromDateTime,
};
